import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Spinner,
  Stack,
  Tag,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type {
  ParticipationQualificationResponseDto,
  ParticipationRequestDto,
  ParticipationRequestPatchDto,
  PortalSourcingEventDetailDto,
  SelectionCriterionDto,
} from "../../../../autogen/bff-api";
import { StepNavigation } from "../../../../common/StepNavigation";
import { useEditor } from "../../../../common/editing/save-coordinator";
import { composeCriterionKey as criterionKey } from "../../../../common/qualification/criterionKey";
import { TipTap } from "../../../../common/input/TipTap/TipTap";

interface Props {
  requestId: string;
  event: PortalSourcingEventDetailDto;
  readOnly: boolean;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

// Stage-1 qualification answers are text per criterion (FOA § 16-12 review is pass/fail on
// these). Evidence uploads ride with the stage-2 tender; here the supplier describes or
// references the documentation instead.
export const RequestQualificationStep = ({
  requestId,
  event,
  readOnly,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<
    ParticipationRequestDto,
    ParticipationRequestPatchDto
  >(requestId);
  const criteria = event.selectionCriteria ?? [];
  const responses = state.qualificationResponses ?? [];

  const upsertResponse = (criterion: SelectionCriterionDto, description: string) => {
    const key = criterionKey(criterion);
    const next: ParticipationQualificationResponseDto[] = [
      ...responses.filter((response) => response.criterionKey !== key),
      { criterionKey: key, description },
    ];
    updateField({ qualificationResponses: next }, { debounceMs: 300 });
  };

  const answerFor = (criterion: SelectionCriterionDto) =>
    responses.find((response) => response.criterionKey === criterionKey(criterion))?.description ?? "";

  const isSaving =
    fieldStatus("qualificationResponses") === "saving" ||
    fieldStatus("qualificationResponses") === "pending";
  const answeredCount = criteria.filter((criterion) => answerFor(criterion).trim().length > 0).length;

  return (
    <Box pb="8" pt="6">
      <Flex align={{ base: "start", md: "center" }} justify="space-between" gap="3" mb="2" flexWrap="wrap">
        <HStack spacing="2" align="center" flexWrap="wrap">
          <Heading size="md">{t("Qualification requirements")}</Heading>
          <Badge bg="smPrimarySofter" color="smPrimary" px="2" py="0.5" borderRadius="md" textTransform="none">
            {t("Buyer criteria")}
          </Badge>
          {isSaving && <Spinner size="sm" color="smMuted" />}
        </HStack>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={answeredCount === criteria.length ? "smSuccess" : "smMuted"}
        >
          {t("{{answered}} of {{total}} answered", { answered: answeredCount, total: criteria.length })}
        </Text>
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="6">
        {t(
          "The buyer assesses these answers pass/fail before inviting qualified suppliers to tender. Reference any documentation in your answer — it can be requested before the verdict.",
        )}
      </Text>

      <Stack spacing="3" mb="6">
        {criteria.map((criterion) => {
          const key = criterionKey(criterion);
          const answered = answerFor(criterion).trim().length > 0;
          return (
            <Box
              key={key}
              borderWidth="1px"
              borderColor={answered ? "smSuccessSoftBorder" : "smBorder"}
              rounded="lg"
              px="4"
              py="3"
              bg={answered ? "smSuccessSoft" : "smBackground"}
            >
              <Stack spacing="3">
                <HStack spacing="2" align="center" flexWrap="wrap">
                  <Tag size="sm" colorScheme="gray" variant="subtle">
                    {t(criterion.type)}
                  </Tag>
                  <Text fontWeight="semibold" fontSize="sm">
                    {criterion.name}
                  </Text>
                  {criterion.requiresEvidence && (
                    <Badge bg="smWarningSoft" color="smWarning" px="2" py="0.5" borderRadius="md" textTransform="none">
                      {t("Documentation required")}
                    </Badge>
                  )}
                </HStack>
                {criterion.description && (
                  <TipTap content={criterion.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
                )}
                <Textarea
                  rows={3}
                  placeholder={String(t("Answer the qualification requirement"))}
                  isReadOnly={readOnly}
                  value={answerFor(criterion)}
                  onChange={(e) => upsertResponse(criterion, e.target.value)}
                />
              </Stack>
            </Box>
          );
        })}
        {criteria.length === 0 && (
          <Text fontSize="sm" color="smMuted">
            {t("The buyer has not defined qualification requirements for this competition.")}
          </Text>
        )}
      </Stack>

      <Box mt="8">
        <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
      </Box>
    </Box>
  );
};
